import { supabase } from '@/integrations/supabase/client';
import { startOfMonth, endOfMonth, format, parseISO, isBefore, isSameMonth, eachMonthOfInterval } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Identifica se um mês está fechado (tem faturamento encerrado)
 * Um mês está fechado quando existe pelo menos uma order (fatura) que cobre aquele mês
 */
export async function getClosedMonths(generatorId?: string): Promise<Date[]> {
  try {
    // Buscar TODAS as orders com paginação para evitar limite de 1000 registros
    let allOrders: any[] = [];
    let from = 0;
    const batchSize = 1000;
    let hasMore = true;

    while (hasMore) {
      let query = supabase
        .from('orders')
        .select('start_date, end_date')
        .order('start_date', { ascending: false });

      // Se houver generator_id, filtrar por ele
      if (generatorId) {
        query = query.eq('generator_id', generatorId);
      }

      const { data: batch, error } = await query.range(from, from + batchSize - 1);

      if (error) {
        console.error('Erro ao buscar orders para identificar meses fechados:', error);
        return [];
      }

      if (batch && batch.length > 0) {
        allOrders = [...allOrders, ...batch];
        from += batchSize;
        
        // Se retornou menos que batchSize, não há mais dados
        if (batch.length < batchSize) {
          hasMore = false;
        }
      } else {
        hasMore = false;
      }
    }

    const orders = allOrders;

    if (!orders || orders.length === 0) {
      return [];
    }

    // Extrair todos os meses únicos que estão cobertos por orders
    const closedMonthsSet = new Set<string>();
    const dateRanges: Array<{ start: string; end: string; months: string[] }> = [];

    orders.forEach((order, index) => {
      // Extrair ano e mês diretamente da string para evitar problemas de timezone
      // Formato esperado: 'YYYY-MM-DD'
      const startMatch = order.start_date.match(/^(\d{4})-(\d{2})/);
      const endMatch = order.end_date.match(/^(\d{4})-(\d{2})/);

      if (!startMatch || !endMatch) {
        return;
      }

      const startYear = parseInt(startMatch[1], 10);
      const startMonth = parseInt(startMatch[2], 10);
      const endYear = parseInt(endMatch[1], 10);
      const endMonth = parseInt(endMatch[2], 10);

      // Gerar todos os meses entre start_date e end_date
      let currentYear = startYear;
      let currentMonth = startMonth;
      const monthsInRange: string[] = [];

      while (
        currentYear < endYear || 
        (currentYear === endYear && currentMonth <= endMonth)
      ) {
        const monthKey = `${currentYear}-${String(currentMonth).padStart(2, '0')}`;
        closedMonthsSet.add(monthKey);
        monthsInRange.push(monthKey);
        
        // Avançar para o próximo mês
        currentMonth++;
        if (currentMonth > 12) {
          currentMonth = 1;
          currentYear++;
        }
      }

    });

    // Converter para array de Date e ordenar (mais antigo primeiro - ordem crescente)
    const closedMonths = Array.from(closedMonthsSet)
      .map(monthKey => {
        const [year, month] = monthKey.split('-').map(Number);
        return new Date(year, month - 1, 1, 12, 0, 0);
      })
      .sort((a, b) => a.getTime() - b.getTime());

    return closedMonths;
  } catch (error) {
    console.error('Erro ao processar meses fechados:', error);
    return [];
  }
}

/**
 * Verifica se um mês específico está fechado
 */
export async function isMonthClosed(month: Date, generatorId?: string): Promise<boolean> {
  const closedMonths = await getClosedMonths(generatorId);
  return closedMonths.some(closedMonth => isSameMonth(closedMonth, month));
}

/**
 * Filtra apenas meses fechados de uma lista de meses
 * Exclui o mês atual (mesmo que esteja fechado)
 */
export function filterClosedMonths(months: Date[], closedMonths: Date[]): Date[] {
  const currentMonth = startOfMonth(new Date());
  
  return months.filter(month => {
    // Excluir mês atual
    if (isSameMonth(month, currentMonth)) {
      return false;
    }

    // Verificar se o mês está na lista de meses fechados
    return closedMonths.some(closedMonth => isSameMonth(closedMonth, month));
  });
}

/**
 * Obtém o último mês fechado disponível
 */
export function getLastClosedMonth(closedMonths: Date[]): Date | null {
  if (closedMonths.length === 0) {
    return null;
  }

  const currentMonth = startOfMonth(new Date());
  
  // Agora que está em ordem crescente, encontrar o último mês fechado que não seja o mês atual
  // Percorrer de trás para frente para pegar o mais recente
  for (let i = closedMonths.length - 1; i >= 0; i--) {
    if (isBefore(closedMonths[i], currentMonth)) {
      return closedMonths[i];
    }
  }
  
  return null;
}
